import { API_BASE_URL, CACHE_SHARE_BASE_URL } from "../../../../constants";
import * as shared from "../../../auth/Onboarding/services/onboardingServices";
import { guardSession, checkAnsweredFor } from "../../../auth/v2/sessionBinding";

// The wizard keeps to the account it opened with (auth/v2/sessionBinding):
// every call checks the stored session first, and the onboarding state must
// answer for that same account.
export { SESSION_CHANGED, bindSession, sessionChanged } from "../../../auth/v2/sessionBinding";

// Server validation answers {error: "invalid", fields: {height: "Enter a number."}}.
// gradYear is the high school class year (the label the edit hub and the
// profile use since the 9/29 review).
const FIELD_LABELS = { jerseyNumber: "Jersey number", height: "Height", weight: "Weight", gradYear: "High school graduation year" };
export const fieldMessage = (fields) => {
  if (!fields || typeof fields !== "object" || Array.isArray(fields)) return "";
  return Object.entries(fields)
    .filter(([, v]) => typeof v === "string" && v.trim())
    .map(([k, v]) => {
      const text = v.trim();
      return FIELD_LABELS[k] ? `${FIELD_LABELS[k]}: ${text[0].toLowerCase()}${text.slice(1)}` : text;
    })
    .join(" ");
};

// A request that never answers (a stalled connection) must not hold a step
// busy forever: a JSON request gives up after DEFAULT_TIMEOUT_MS, longer
// than the 30 seconds the host lets any request run, and the score refresh
// the team steps wait on (recompute, then the polls) after
// SCORE_REFRESH_TIMEOUT_MS. Uploads keep no limit. A request that gives up
// throws with status 0 and `timedOut`.
const DEFAULT_TIMEOUT_MS = 45000;
export const SCORE_REFRESH_TIMEOUT_MS = 20000;
export const TIMED_OUT_MESSAGE = "The connection timed out. Try again.";
const timedOut = () => {
  const err = new Error(TIMED_OUT_MESSAGE);
  err.status = 0;
  err.timedOut = true;
  return err;
};

// What a failed request says (10/19 notes). The server's own sentence for a
// refusal (a 4xx: a validation note, "That code is invalid"); a neutral
// sentence when team data is down (code provider_unavailable); and never a
// 5xx body's text, which can carry a data vendor's name or a raw error.
export const PROVIDER_UNAVAILABLE = "provider_unavailable";
export const PROVIDER_UNAVAILABLE_MESSAGE = "Team data is not available right now. Try again in a minute.";
export const SERVER_ERROR_MESSAGE = "Something went wrong on our side. Try again in a minute.";
export const errorMessageOf = (status, data) => {
  if (data?.code === PROVIDER_UNAVAILABLE) return PROVIDER_UNAVAILABLE_MESSAGE;
  if (!status || status >= 500) return SERVER_ERROR_MESSAGE;
  return data?.message || fieldMessage(data?.fields) || data?.error || data?.detail || `HTTP ${status}`;
};

const call = async (method, path, body, { base = API_BASE_URL, multipart = false, timeoutMs = multipart ? 0 : DEFAULT_TIMEOUT_MS } = {}) => {
  const auth = { Authorization: `Bearer ${guardSession()}` };
  const ctrl = timeoutMs > 0 && typeof AbortController === "function" ? new AbortController() : null;
  const timer = ctrl ? setTimeout(() => ctrl.abort(), timeoutMs) : null;
  try {
    let res;
    try {
      res = await fetch(`${base}/${path}`, {
        method,
        headers: multipart ? auth : { ...auth, "Content-Type": "application/json" },
        body: body === undefined ? undefined : multipart ? body : JSON.stringify(body),
        ...(ctrl ? { signal: ctrl.signal } : {}),
      });
    } catch (e) {
      if (ctrl?.signal.aborted) throw timedOut();
      throw e;
    }
    let data = {};
    try { data = await res.json(); } catch { data = {}; }
    // A body cut off by the time limit is not an empty answer.
    if (ctrl?.signal.aborted) throw timedOut();
    if (!res.ok) {
      const err = new Error(errorMessageOf(res.status, data));
      err.status = res.status;
      err.code = typeof data?.code === "string" ? data.code : "";
      err.retryable = data?.code === PROVIDER_UNAVAILABLE || res.status >= 500;
      err.data = data;
      throw err;
    }
    if (path === "athlete/onboarding/") checkAnsweredFor(data);
    return data;
  } finally {
    if (timer) clearTimeout(timer);
  }
};

// The shared onboarding endpoints (axios) read the token themselves; check
// the session first so they never run for another account either.
const guarded = (name) => async (...args) => {
  guardSession();
  return shared[name](...args);
};
export const previewCoachCode = guarded("previewCoachCode");
export const linkCoachCode = guarded("linkCoachCode");
export const requestTeam = guarded("requestTeam");
export const fetchOnboardingCandidates = guarded("fetchOnboardingCandidates");
export const completeOnboarding = guarded("completeOnboarding");

// One read of everything the wizard shows; one narrow write per step.
// `opts.timeoutMs` shortens the read's time limit (the score refresh polls).
export const getOnboarding = (opts) => call("GET", "athlete/onboarding/", undefined, opts);
export const patchOnboarding = (patch) => call("PATCH", "athlete/onboarding/", patch);
export const saveProgress = (step, done = []) => patchOnboarding({ progress: { step, done } });

// Team search across every league (the "Any league" scope) by name. Partner
// teams that are not linked to an API program come back too (they carry
// cacheTeamId and cacheLeague), so a team an admin added can be picked.
// Rows carry displayName, ageGroup, season and programOnly. While the team
// feed is down the search still answers, with the teams already on CACHE
// and dataStatus {state: "unavailable" | "stale", message}: searchTeamsDetailed
// returns { teams, dataStatus, limited }, and searchTeams the rows alone
// (dataStatus rides along as a non-enumerable property).
export const searchTeamsDetailed = async (query, gender) => {
  const data = await call("POST", "cerebro/live/teams/", { cerebroSports: ["cbx_any"], teamQuery: query, gender, includePartners: true }, { base: CACHE_SHARE_BASE_URL });
  const teams = Array.isArray(data?.teams) ? data.teams : [];
  const dataStatus = data?.dataStatus && typeof data.dataStatus === "object" ? data.dataStatus : null;
  return { teams, dataStatus, limited: !!dataStatus && dataStatus.state !== "live" };
};

export const searchTeams = async (query, gender) => {
  const { teams, dataStatus } = await searchTeamsDetailed(query, gender);
  Object.defineProperty(teams, "dataStatus", { value: dataStatus, enumerable: false });
  return teams;
};

// The state codes (type state) for the hometown State picker, by name.
export const fetchStateCodes = async () => {
  const rows = await call("GET", "codes/all/");
  return (Array.isArray(rows) ? rows : [])
    .filter((c) => c && c.type === "state" && c.value != null && c.name)
    .sort((x, y) => String(x.name).localeCompare(String(y.name)));
};

// Earlier profiles that look like this athlete's (strong or likely pilot
// matches, public details only), and "this is me", which only asks an admin
// to review the pair.
export const fetchEarlierProfiles = async () => {
  const data = await call("GET", "athletes/duplicates/mine/");
  return Array.isArray(data?.candidates) ? data.candidates : [];
};
export const claimEarlierProfile = (candidateId) => call("POST", "athletes/duplicates/claim/", { candidateId });

export const uploadAvatar = async (file, email) => {
  const form = new FormData();
  form.append("avatar", file);
  form.append("email", email);
  return call("POST", "athlete/avatar/upload/", form, { multipart: true });
};

export const createCheckout = (interval = "monthly") =>
  call("POST", "stripe/create-checkout-session/", { tier: "ATHLETE_PREMIUM", interval, returnTo: "onboarding" });

export const redeemPromo = (code) => call("POST", "promocodes/redeem/", { code });

export const fetchEntitlementNow = () => call("GET", "entitlement/");

// The server only starts the refresh, so a slow answer is not waited on for
// long (SCORE_REFRESH_TIMEOUT_MS); callers treat it as best effort.
export const recomputeSelf = () => call("POST", "athlete/recompute-self/", {}, { timeoutMs: SCORE_REFRESH_TIMEOUT_MS });

// Poll the onboarding state until `ready(state)` is true or time runs out.
// `alive` (optional) is checked before every read; once it is false the poll
// stops and answers { stopped: true }, so a wizard that is gone (unmounted or
// logged out) sends nothing more under whoever signs in next. `timeoutMs`
// (optional) is each read's time limit; a read that gives up throws.
export const waitFor = async (ready, { tries = 12, delayMs = 2500, alive, timeoutMs } = {}) => {
  let last = null;
  for (let i = 0; i < tries; i += 1) {
    if (typeof alive === "function" && !alive()) return { state: last, ok: false, stopped: true };
    last = await getOnboarding(timeoutMs ? { timeoutMs } : undefined);
    if (ready(last)) return { state: last, ok: true };
    await new Promise((r) => setTimeout(r, delayMs));
  }
  return { state: last, ok: false };
};

// Shrink a photo before upload (same approach the profile editor uses).
export const compressImage = (file, maxSide = 1024, quality = 0.85) =>
  new Promise((resolve) => {
    if (!file.type.startsWith("image/")) { resolve(file); return; }
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => {
        URL.revokeObjectURL(url);
        resolve(blob ? new File([blob], file.name.replace(/\.\w+$/, ".jpg"), { type: "image/jpeg" }) : file);
      }, "image/jpeg", quality);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });
